import { feedbackLine, answerFeedback } from "../fuxi/yufa-answer-feedback.js";
import {
  chooseEmptyLine,
  chooseRightLine,
  chooseWrongLine,
  gapEmptyLine,
  gapOtherWritingLine,
  gapRightLine,
  gapWrongLine,
  orderEmptyLine,
  orderUnverifiedLine,
  orderRightLine,
  orderWrongLine,
  rewriteEmptyLine,
  rewriteOtherLine,
  rewriteRightLine,
  rewriteWrongLine,
} from "./lianxi-yufa-copy.js";
import { requirePart } from "./lianxi-yufa-item.js";
import { markItemOnPage } from "./lianxi-yufa-item-view.js";
import {
  type ItemAnswer,
  markItem,
  nearlyRight,
  passes,
} from "../fuxi/yufa-marking.js";

/**
 * Which line a grammar exercise says about an answer, and where an empty
 * answer is said. `./lianxi-yufa-copy.ts` has the lines,
 * `src/fuxi/yufa-marking.ts` the marking, and `./lianxi-yufa.ts` the
 * exercise that asks for both.
 */

/** What is said about an item answered with nothing, by the item's kind. */
const emptyLines: Readonly<Record<string, string>> = {
  gap: gapEmptyLine,
  arrange: orderEmptyLine,
  choose: chooseEmptyLine,
  transform: rewriteEmptyLine,
  contrast: "Pick a word or Nothing needed first.",
  placement: "Pick an insertion position first.",
  interpretation: "Choose an interpretation first.",
  reply: "Choose a reply first.",
  repair:
    "Select the part to change and type your corrected sentence, or choose No change needed.",
};

/** What is said about a marked answer, by its kind: right, nearly, wrong. */
const markedLines: Readonly<
  Record<Exclude<ItemAnswer["kind"], "gap">, readonly [string, string, string]>
> = {
  arrange: [orderRightLine, orderRightLine, orderWrongLine],
  choose: [chooseRightLine, chooseRightLine, chooseWrongLine],
  transform: [rewriteRightLine, rewriteOtherLine, rewriteWrongLine],
  contrast: [chooseRightLine, chooseRightLine, chooseWrongLine],
  placement: [chooseRightLine, chooseRightLine, chooseWrongLine],
  interpretation: [chooseRightLine, chooseRightLine, chooseWrongLine],
  reply: [chooseRightLine, chooseRightLine, chooseWrongLine],
  repair: [rewriteRightLine, rewriteOtherLine, rewriteWrongLine],
};

/**
 * Say that nothing is answered yet, without marking anything.
 * @param form - The item.
 */
export function markEmpty(form: HTMLFormElement): void {
  requirePart(form, "mark").textContent =
    emptyLines[form.dataset["cbYufaKind"] ?? ""] ?? gapEmptyLine;
}

/**
 * What to say about a marked answer.
 * @param answer - What the reader produced.
 * @param passed - Whether it passed the item.
 * @param nearly - Whether it was right in another form.
 * @param unverified - Whether the order is outside the checked answers.
 * @returns The line.
 */
export function said(
  answer: ItemAnswer,
  passed: boolean,
  nearly: boolean,
  unverified = false,
): string {
  if (unverified) {
    return orderUnverifiedLine;
  }

  if (answer.kind === "gap") {
    return passed
      ? nearly
        ? gapOtherWritingLine(answer.word)
        : gapRightLine
      : gapWrongLine(answer.word);
  }

  const [right, other, wrong] = markedLines[answer.kind];

  return passed ? (nearly ? other : right) : wrong;
}

/**
 * Mark an answer and say what it was on the item.
 * @param form - The item.
 * @param answer - What the reader produced.
 * @returns Whether the answer passed the item.
 */
export function markShown(form: HTMLFormElement, answer: ItemAnswer): boolean {
  const graded = markItem(answer);
  const passed = passes(graded);

  markItemOnPage(
    form,
    feedbackLine(answer, passed) ??
      said(
        answer,
        passed,
        nearlyRight(graded),
        graded.outcome === "unverified",
      ),
    passed,
    graded.outcome === "unverified" ||
      answerFeedback(answer)?.status === "valid-other" ||
      (answer.kind === "repair" &&
        !passed &&
        answerFeedback(answer)?.status === "target"),
  );

  return passed;
}
